import type { createInterfaceLanguageController } from "./interfaceLanguageController";
import {
  getNativeInterfaceLanguageContext, postNativeGuestInterfacePreference, subscribeNativeInterfaceLanguageContext,
} from "./nativeInterfaceLanguages";

/** Account writes remain in the profile API. Only settled guest intent crosses back. */
export function connectNativeInterfaceLanguage({ controller, accountId, onError = () => undefined }: {
  controller: Pick<ReturnType<typeof createInterfaceLanguageController>, "getSnapshot" | "subscribe" | "refreshSystemLanguage">;
  accountId: string | null;
  onError?: (error: unknown) => void;
}) {
  let lastLanguages: string | null = null;
  let lastGuestChoice: string | null = null;
  let synchronizing = false;
  const synchronize = () => {
    if (synchronizing) return;
    const context = getNativeInterfaceLanguageContext();
    if (!context) return;
    synchronizing = true;
    try {
      const languages = JSON.stringify(context.languages);
      if (languages !== lastLanguages) {
        lastLanguages = languages;
        void controller.refreshSystemLanguage();
      }
      const state = controller.getSnapshot();
      const isGuestOnBothSides = accountId === null && state.accountId === null && context.accountId === null;
      const hasSettledGuestIntent = state.status === "idle" && !state.error &&
        (state.source === "guest" || state.source === "system");
      // Account authority and unsettled or failed choices must never become native guest intent.
      if (!isGuestOnBothSides || !hasSettledGuestIntent) return;
      const choice = JSON.stringify([context.session, state.preference]);
      if (choice !== lastGuestChoice && postNativeGuestInterfacePreference(context, state.preference)) lastGuestChoice = choice;
    } catch (error) {
      try { onError(error); } catch { /* Diagnostics cannot break locale reconciliation. */ }
    }
    finally { synchronizing = false; }
  };
  const unsubscribeController = controller.subscribe(synchronize);
  const unsubscribeNative = subscribeNativeInterfaceLanguageContext(synchronize);
  synchronize();
  return () => { unsubscribeController(); unsubscribeNative(); };
}
